import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { DrilldownDialogProps } from '@databox/ember-integrations/lib/components/Datasets/DrilldownDialog/DrilldownDialog';

import type { DChartInterface, DSData, PopupData, Service } from 'webapp-types';
import { TrackingRawDataFeature } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import RawDataPopupComponent from 'webapp/components/raw-data/popup';

import type { Block, DataSelect } from 'webapp/models-custom';
import type { MetricSelectionData } from 'webapp/models-custom/ds-data/output/top';
import MetricSetup from 'webapp/models-custom/metric-setup';

export default class MetricRawDataService extends EmberService {
	@service('request') declare requestService: Service.Request;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('popup') declare popupService: Service.Popup;

	showRawDataDialog?: (props: DrilldownDialogProps) => void;
	@tracked showRawDataContainer = false;

	requestDrillDownDataForSelectedPoint(
		dataSelect: DataSelect,
		point: {
			granulationLabel: DSData.GranulationLabel | null;
			metricSelectionData?: Partial<MetricSelectionData> | null;
		},
		e: PointerEvent,
	) {
		const metricSetup = dataSelect?.metricSetup;

		if (!metricSetup) {
			return;
		}

		const pointOnChart = (e as Highcharts.PointClickEventObject)
			.point as DChartInterface.ChartHoverPoint;

		let granulationLabel = null;
		let requestedDimensionsAndValuesMap: DSData.DimensionsAndValuesMap | undefined;
		let dataPointMetricSetup: MetricSetup;

		if (pointOnChart?.dbxPoint) {
			dataPointMetricSetup = metricSetup.createCopy();

			requestedDimensionsAndValuesMap =
				pointOnChart.dbxPoint.metricSelectionData?.dimensionValues;
			granulationLabel = pointOnChart.dbxPoint.granulationLabel;
		} else {
			const metricSelectionData = point.metricSelectionData;

			dataPointMetricSetup = new MetricSetup(
				metricSelectionData?.metricSetupData || undefined,
			);

			requestedDimensionsAndValuesMap = metricSelectionData?.dimensionValues;
			granulationLabel = point.granulationLabel;
		}

		if (!granulationLabel) {
			return;
		}

		// preparing metric setup data for request
		// if attribute is requested, add it as another dimension type filter
		// and always remove unit filter group

		if (
			requestedDimensionsAndValuesMap &&
			Object.keys(requestedDimensionsAndValuesMap).length
		) {
			dataPointMetricSetup.setFiltersFromDimensionsAndValuesMapAndUnit(
				requestedDimensionsAndValuesMap,
			);
		}
		dataPointMetricSetup.unitName = null;

		this.requestDataForRawDataPoint(
			dataPointMetricSetup,
			granulationLabel,
			this.getTrackingRawDataFeature(dataSelect.block),
		);
	}

	private requestDataForRawDataPoint(
		metricSetup: MetricSetup,
		granulationLabel: DSData.GranulationLabel,
		trackingFeature: TrackingRawDataFeature,
	) {
		this.showRawDataContainer = true;

		const { metric, source, attributeNamesFromBasicFilters } = metricSetup;

		const { ts, endTs, label } = granulationLabel;
		const startTimestamp = ts;
		const endTimestamp = endTs;

		const sendRequest = (
			params: {
				page: number;
				pageSize: number;
				column?: string;
				order?: string;
			},
			period: [number, number] = [startTimestamp, endTimestamp],
		) => {
			return this.requestService.api<Awaited<ReturnType<PopupData.RawData.Popup['request']>>>(
				'POST',
				this.buildDataURLWithParams(params),
				{
					metricSetup: metricSetup.metricSetupData,
					period: {
						startTimestamp: period[0],
						endTimestamp: period[1],
					},
				},
			);
		};

		const exportRequest = async (period?: [number, number]) => {
			await this.requestService.api(
				'POST',
				'/data-exploration/drilldown/export',
				{
					metricSetup: metricSetup.metricSetupData,
					period: {
						startTimestamp: period ? period[0] : startTimestamp,
						endTimestamp: period ? period[1] : endTimestamp,
					},
				},
				{
					requestId: crypto.randomUUID(),
				},
			);
		};

		const filteredBy = attributeNamesFromBasicFilters.map((item) => item).join(',');

		const popup = this.popupService.createModal(RawDataPopupComponent, {
			popupClass: 'popup-type-drawer popup-type-raw-data',
			showClose: false, // handled in base drawer component
			hasOverlay: true,
			closeOnOverlayClick: false,
			allowStacked: true,
			isAutoCentered: false,
		});

		const props: PopupData.RawData.Popup = {
			metricName: metric?.name ?? '',
			source: source!,
			period: {
				title: label,
				startDate: new Date(startTimestamp * 1000),
				endDate: new Date(endTimestamp * 1000),
			},
			filteredBy: filteredBy.length > 0 ? filteredBy : undefined,
			request: sendRequest,
			exportRequest: exportRequest,
		};

		popup.open(props);

		this.analyticsService.event(TrackingEvent.METRIC_DATA_RAW_POPUP, {
			feature: trackingFeature,
		});
	}

	private buildDataURLWithParams(queryParams: Record<string, string | number | undefined>) {
		const url = '/data-exploration/drilldown',
			params = new URLSearchParams();

		for (const [key, value] of Object.entries(queryParams)) {
			if (value !== undefined && value !== null) {
				params.append(key, value.toString());
			}
		}

		return `${url}?${params.toString()}`;
	}

	private getTrackingRawDataFeature(block: Block | null) {
		if (block?.isFromMetricInsight) {
			return TrackingRawDataFeature.METRIC_INSIGHTS;
		}

		if (block?.isFromReport) {
			return TrackingRawDataFeature.REPORT;
		}

		return TrackingRawDataFeature.DATABOARD;
	}
}

declare module '@ember/service' {
	interface Registry {
		'metric-raw-data': MetricRawDataService;
	}
}
